import React from 'react'

import classNames from 'classnames'

import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { ImageProps } from '~/components/atoms/Image/Image'
import SpecificImage from '~/components/atoms/SpecificImage/SpecificImage'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from './ImageGrid.module.css'

interface ImageGridProps {
  theme?: ComponentTheme
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  gridStyle?: 'variant-1' | 'variant-2' | ''
  image1: ImageProps
  image2?: ImageProps
  image3?: ImageProps
  image4?: ImageProps
  moduleId?: string
  imageCredit?: string
}

export default function ImageGrid({
  image1,
  image2,
  image3,
  image4,
  gridStyle = '',
  theme = ComponentTheme.primary,
  verticalPadding = true,
  noPaddingBottom,
  moduleId,
  imageCredit,
}: ImageGridProps) {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)

  const renderImages = () => {
    if (!gridStyle) {
      if (image1 && image2) {
        return (
          <div className={styles.twoImagesGrid}>
            <div className={styles.gridImage}>
              <SpecificImage
                asset={image1.asset}
                alt={'Image Grid 1'}
                width={isMobile ? 400 : 800}
              />
            </div>
            <div className={styles.gridImage}>
              <SpecificImage
                asset={image2.asset}
                alt={'Image Grid 2'}
                width={isMobile ? 400 : 800}
              />
            </div>
          </div>
        )
      } else if (image1) {
        return (
          <div className={styles.singleImage}>
            <SpecificImage
              asset={image1.asset}
              alt={'Image Grid 1'}
              width={isMobile ? 400 : 1600}
            />
            {imageCredit && (
              <Text
                Tag="p"
                size={TextSizes.tertiaryTitleSml}
                className={styles.imageCredit}
              >
                {imageCredit}
              </Text>
            )}
          </div>
        )
      }
    }

    return (
      <div
        className={classNames(styles.images, {
          [styles.reverse]: gridStyle === 'variant-2',
        })}
      >
        <div className={styles.leftImage}>
          <SpecificImage
            asset={image1.asset}
            alt={'Image Grid 1'}
            width={isMobile ? 400 : 800}
          />
        </div>
        <div className={styles.rightImages}>
          <div className={styles.topRightImages}>
            {image2 && (
              <SpecificImage
                asset={image2.asset}
                alt={'Image Grid 2'}
                width={400}
              />
            )}
            {image3 && (
              <SpecificImage
                asset={image3.asset}
                alt={'Image Grid 3'}
                width={400}
              />
            )}
          </div>
          {image4 && (
            <div className={styles.bottomRightImage}>
              <SpecificImage
                asset={image4.asset}
                alt={'Image Grid 4'}
                width={isMobile ? 400 : 800}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <Fixture
      verticalPadding={verticalPadding}
      theme={theme}
      noPaddingBottom={noPaddingBottom}
      id={moduleId}
    >
      <div className={styles.base}>
        <Container>
          <div className={styles.container}>{renderImages()}</div>
        </Container>
      </div>
    </Fixture>
  )
}
